<style lang="scss" scoped>
.player-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background: #000;
  display: flex;

  #watchClapprPlayer {
    width: 100%;
    height: 100%;
  }
}
</style>
<template>
  <div class="player-wrapper">
    <div id="watchClapprPlayer"> </div>
  </div>
</template>
<script>
import {
  h,
  ref,
  onMounted,
  defineComponent,
  onBeforeUnmount,
  nextTick,
  watch
} from "@vue/composition-api";
import { Player, Events, Log } from "@clappr/core";

import {
  ClickToPause,
  ClosedCaptions,
  DVRControls,
  EndVideo,
  ErrorScreen,
  Favicon,
  GoogleAnalytics,
  MediaControl,
  Poster,
  SeekTime,
  SpinnerThreeBounce,
  Stats,
  WaterMark,
} from "@clappr/plugins";
import QualitySelector from "./lib/quality-selector.min.js";
import HlsjsPlayback from "@clappr/hlsjs-playback";
import Hls from "hls.js";
import DashShakaPlayback from "dash-shaka-playback";
import CryptoJS from "crypto-js";


function process(playlist, url) {
  const urlObj = new URL(url);
  var bytes = CryptoJS.AES.decrypt(playlist, `${urlObj.pathname}${urlObj.search ? urlObj.search : ''}`);
  var originalText = bytes.toString(CryptoJS.enc.Utf8);
  return originalText;
}

class pLoader extends Hls.DefaultConfig.loader {
  constructor(config) {
    super(config);
    var load = this.load.bind(this);
    this.load = function (context, config, callbacks) {
      if (context.type == 'manifest' || context.type == 'level' || context.type == 'audioTrack') {
        var onSuccess = callbacks.onSuccess;
        callbacks.onSuccess = function (response, stats, context) {
          response.data = process(response.data, response.url);
          onSuccess(response, stats, context);
        };
      }
      load(context, config, callbacks);
    };
  }
}

export default defineComponent({
  name: "VClappr",
  props: {
    type: {
      type: String,
      default: "hls",
    },
    src: {
      type: String,
      default: "",
    },
    initial: {
      type: [Number, String],
      default: 0,
    },
    muted: {
      type: Boolean,
      default: false,
    },
    encypted: {
      type: Boolean,
      default: false,
    },
    el: {
      type: String,
      default: "watchClapprPlayer",
    },
  },
  setup(props, { emit }) {
    // Player Init Clappr Register Events
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const player = ref(null);
    const isFullScreen = ref(false);

    onMounted(async () => {
      await nextTick();
      await initClapprPlayer();
    });

    onBeforeUnmount(() => {
      player.value.destroy();
      player.value = null;
    });

    watch(
      () => props.src,
      async (newVal, oldVal) => {
        if (newVal !== oldVal) {
          if (player.value) {
            player.value.load(url);
          }
        }
      }
    );

    /**
     * Initialize the clappr player
     *
     * @returns {Promise<void>}
     */
    function initClapprPlayer() {
      // Return a new promise
      return new Promise((resolve, reject) => {
        if (props.el) {
          Log.setLevel(5);
          // New Player instance
          player.value = new Player({
            parentId: `#${props.el}`,
            source: props.src,
            plugins: [
              ClickToPause,
              ClosedCaptions,
              DVRControls,
              EndVideo,
              ErrorScreen,
              Favicon,
              GoogleAnalytics,
              MediaControl,
              Poster,
              SeekTime,
              SpinnerThreeBounce,
              Stats,
              WaterMark,
              QualitySelector,
              HlsjsPlayback,
              DashShakaPlayback,
            ],
            qualitySelectorConfig: {
              title: "QUALITY",
              labels: {
                5: "SUPER HD",
                4: "FULL HD",
                3: "HD",
                2: "HIGH",
                1: "MEDIUM",
                0: "LOW",
              },
              defaultQuality: -1, // start with quality 0 or Lowest
              labelCallback: function (playbackLevel, customLabel) {
                return customLabel + " " + playbackLevel.level.height + "p"; // High 720p
              },
            },
            width: "100%",
            height: "100%",
            autoPlay: true,
            mute: props.muted,
            loop: false,
            language: "en-US",
            playbackNotSupportedMessage: "Sorry, Something went wrong. Reload the page and try again.",
            autoSeekFromUrl: false,
            includeResetStyle: true,
            playback: {
              preload: "metadata",
              disableContextMenu: true,
              controls: false,
              crossOrigin: null,
              playInline: true,
              minimumDvrSize: null,
              externalTracks: [],
              extrapolatedWindowNumSegments: 10,
              triggerFatalErrorOnResourceDenied: false,
              hlsjsConfig: {
                debug: false,
                enableWorker: true,
                lowLatencyMode: true,
                liveSyncDuration: 60,
                maxBufferSize: 2000,
                backBufferLength: 600,
                //pLoader: Hls.DefaultConfig.loader,
                //loader: engine.createLoaderClass()
              },
              shakaConfiguration: {},
            },
            poster: "",
          });

          nextTick(() => {
            emit("init", player.value);

            player.value.seek(props.initial);
            player.value.listenTo(
              player.value.core.mediaControl,
              Events.MEDIACONTROL_HIDE,
              () => {
                emit("hideMediaControl");
              }
            );

            player.value.listenTo(
              player.value.core.mediaControl,
              Events.MEDIACONTROL_SHOW,
              () => {
                emit("showMediaControl");
              }
            );

            // fullscreen
            player.value.listenTo(
              player.value.core.mediaControl,
              Events.MEDIACONTROL_FULLSCREEN,
              (e) => {
                isFullScreen.value = !isFullScreen.value;
                if (isFullScreen.value) {
                  screen.orientation.lock("landscape-primary");
                } else {
                  screen.orientation.lock("portrait-primary");
                }
                emit("fullscreen", isFullScreen.value);
              }
            );

            player.value.configure({
              events: {
                onReady: () => {
                  emit("ready", player.value);
                },
                onResize: (e) => {
                  emit("resize", e);
                },
                onPlay: () => {
                  emit("play", true);
                },
                onPause: () => {
                  emit("pause", true);
                },
                onStop: (e) => {
                  emit("stop", e);
                },
                onEnded: () => {
                  emit("ended", true);
                },
                onSeek: (e) => {
                  emit("seek", e);
                },
                onError: (e) => {
                  emit("error", e);
                },
                onTimeUpdate: (e) => {
                  emit("time-updated", e);
                  emit("progress", e);
                },
                onVolumeUpdate: (e) => {
                  emit("volume-updated", e);
                },
                onSubtitleAvailable: (e) => {
                  emit("subtitle-available", e);
                },


              },
              allowUserInteraction: false,
            });

          });
          resolve();
        } else {
          reject("Root element not found");
        }
      });
    }

  },
});
</script>